import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import {useMemo} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {accessibleObjs, getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import {
  SamplesExistingTestOrdersQuery,
  SamplesExistingTestOrdersQueryVariables,
} from 'sample-tests/ui/test-orders/modals/__generated/SamplesExistingTestOrdersQuery';

export type AccessibleBioentity = ExcludeInaccessible<SamplesExistingTestOrdersQuery['bioentities'][number]>;
type ExistingTestOrder = AccessibleBioentity['testOrders'][number];
export interface ExistingTestOrdersForSampleTestDefinition {
  [sampleId: string]: {
    name: string;
    testOrdersByTestDefinition: {[testDefinitionId: string]: Array<ExistingTestOrder>};
  };
}

export const SAMPLES_EXISTING_TEST_ORDERS_QUERY: DocumentNode = gql`
  query SamplesExistingTestOrdersQuery($sampleIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: PROCEDURES) {
    bioentities(ids: $sampleIds) {
      ... on RegistryItemInterface {
        id
        name
        testOrders(statusIsOneOf: [PENDING, BLOCKED, IN_PROGRESS]) {
          id
          name
          testDefinitionCorrect {
            ... on TestDefinitionNode {
              id
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function useExistingTestOrdersForSamples(
  sampleIds: Array<string>
): ExistingTestOrdersForSampleTestDefinition | undefined {
  const samplesResult = useBenchlingQuery<
    SamplesExistingTestOrdersQuery,
    SamplesExistingTestOrdersQueryVariables
  >(SAMPLES_EXISTING_TEST_ORDERS_QUERY, {variables: {sampleIds}, fetchPolicy: 'cache-and-network'});
  // Data must be pulled out separately for the memoization to work properly
  const data = samplesResult.type === QueryResultType.SUCCESS ? samplesResult.data : undefined;
  return useMemo(() => {
    if (samplesResult.type !== QueryResultType.SUCCESS) {
      return undefined;
    }

    const accessibleBioentities = accessibleObjs(data!.bioentities);
    // Get existing test orders organized by each sample + test definition combo
    return accessibleBioentities.reduce(
      (existingTestOrderMapping: ExistingTestOrdersForSampleTestDefinition, sample: AccessibleBioentity) => {
        existingTestOrderMapping[sample.id] ??= {name: sample.name, testOrdersByTestDefinition: {}};
        sample.testOrders.forEach((testOrder) => {
          const testDefId = getAccessibleOrInaccessibleId(testOrder.testDefinitionCorrect);
          existingTestOrderMapping[sample.id].testOrdersByTestDefinition[testDefId] ??= [];
          existingTestOrderMapping[sample.id].testOrdersByTestDefinition[testDefId].push(testOrder);
        });
        return existingTestOrderMapping;
      },
      {}
    );
  }, [samplesResult.type, data]);
}
